
const IV_LENGTH = 16;

const base64ToUint8Array = (
    base64: string
): Uint8Array => {
    const binary = atob(base64);

    const bytes = new Uint8Array(
        binary.length
    );

    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return bytes;
};

const uint8ArrayToBase64 = (
    bytes: Uint8Array
): string => {
    let binary = '';

    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
};

const generateKey = async (
    secretKey: string
): Promise<CryptoKey> => {

    const keyMaterial =
        await crypto.subtle.digest(
            'SHA-256',
            new TextEncoder().encode(
                secretKey
            )
        );

    return crypto.subtle.importKey(
        'raw',
        keyMaterial,
        {
            name: 'AES-GCM'
        },
        false,
        ['encrypt', 'decrypt']
    );
};

/**
 * Encrypt any JSON-serializable payload with AES-256-GCM.
 * Output format matches the backend (`service/encrypt.mjs`):
 *   iv(base64):authTag(base64):cipherText(base64)
 *
 * WebCrypto returns ciphertext+authTag concatenated, so we split the
 * trailing 16-byte (128-bit) tag back out to keep the same wire format.
 */
export const encryptResponse = async (
    payload: unknown,
    secretKey: string
): Promise<string> => {
    try {
        const cryptoKey = await generateKey(secretKey);

        const iv = crypto.getRandomValues(
            new Uint8Array(IV_LENGTH)
        );

        const plainText =
            typeof payload === 'string'
                ? payload
                : JSON.stringify(payload);

        const encrypted = new Uint8Array(
            await crypto.subtle.encrypt(
                {
                    name: 'AES-GCM',
                    iv: iv as BufferSource,
                    tagLength: 128
                },
                cryptoKey,
                new TextEncoder().encode(plainText) as BufferSource
            )
        );

        // The last 16 bytes are the GCM auth tag.
        const tagLengthBytes = 16;
        const cipherText = encrypted.slice(
            0,
            encrypted.length - tagLengthBytes
        );
        const authTag = encrypted.slice(
            encrypted.length - tagLengthBytes
        );

        return [
            uint8ArrayToBase64(iv),
            uint8ArrayToBase64(authTag),
            uint8ArrayToBase64(cipherText)
        ].join(':');
    } catch (error) {
        const message =
            error instanceof Error
                ? error.message
                : 'Unknown encryption error';

        throw new Error(`Encryption failed: ${message}`);
    }
};

/**
 * Decrypt an `iv:authTag:cipherText` string and return the raw decoded
 * plaintext string (no JSON parsing). Use this when the original payload
 * was already a string (e.g. the redux-persist serialized state).
 */
export const decryptString = async (
    encryptedString: string,
    secretKey: string
): Promise<string> => {

    const [
        ivBase64,
        authTagBase64,
        cipherTextBase64
    ] = encryptedString.split(':');

    if (
        !ivBase64 ||
        !authTagBase64 ||
        !cipherTextBase64
    ) {
        throw new Error(
            'Invalid encrypted payload format'
        );
    }

    const cryptoKey =
        await generateKey(
            secretKey
        );

    const iv =
        base64ToUint8Array(
            ivBase64
        );

    const authTag =
        base64ToUint8Array(
            authTagBase64
        );

    const cipherText =
        base64ToUint8Array(
            cipherTextBase64
        );

    // WebCrypto expects:
    // ciphertext + authTag
    const encryptedBuffer =
        new Uint8Array(
            cipherText.length +
            authTag.length
        );

    encryptedBuffer.set(
        cipherText,
        0
    );

    encryptedBuffer.set(
        authTag,
        cipherText.length
    );

    const decrypted =
        await crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: iv as BufferSource,
                tagLength: 128
            },
            cryptoKey,
            encryptedBuffer as BufferSource
        );

    return new TextDecoder().decode(decrypted);
};

export const decryptResponse = async <T>(
    encryptedString: string,
    secretKey: string
): Promise<T> => {

    try {
        const decoded = await decryptString(
            encryptedString,
            secretKey
        );

        try {
            return JSON.parse(
                decoded
            ) as T;
        } catch {
            return decoded as T;
        }

    } catch (error) {

        const message =
            error instanceof Error
                ? error.message
                : 'Unknown decryption error';

        throw new Error(
            `Decryption failed: ${message}`
        );
    }
};